// src/pages/Inventory/VehicleHsdHourUpdationListing.jsx
// Old ASPX: VehicleHsdHourUpdationListing.aspx  (INFRA SCREEN : A-39/1)
// Design aligned with ProductionBOMListing.jsx template.
//
// Backend:
//   app.use("/api/vehicleHsdHrUpdation", vehicleHsdHrUpdation);
//
// APIs:
//   GET  /api/vehicleHsdHrUpdation/init?branchId=8&branchType=HO
//   GET  /api/vehicleHsdHrUpdation/vehicleCodes?branch=8
//   GET  /api/vehicleHsdHrUpdation/list?branch=8&fromDate=2026-02-01&toDate=2026-02-24&vehicleCode=Select
//   GET  /api/vehicleHsdHrUpdation/export?branch=8&fromDate=...&toDate=...&vehicleCode=...

import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  FaSearch,
  FaFileExcel,
  FaChevronDown,
  FaChevronUp,
  FaArrowLeft,
  FaPlus,
} from "react-icons/fa";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const BASE_URL = import.meta?.env?.VITE_BASE_URL || "https://nissi-india.com";
const API_BASE = `${BASE_URL}/api/vehicleHsdHrUpdation`;

/* ---------------- helpers ---------------- */
const toInt = (v, def = 0) => {
  const n = parseInt(String(v ?? "").trim(), 10);
  return Number.isFinite(n) ? n : def;
};

const toStr = (v, def = "") => {
  if (v === null || v === undefined) return def;
  return String(v).trim();
};

const clean = (v) => String(v ?? "").trim();

const isSelect = (v) => !toStr(v) || toStr(v).toLowerCase() === "select";

const pad = (n) => String(n).padStart(2, "0");

const toYYYYMMDD = (d) => {
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "";
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
};

const sixMonthsBack = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 6);
  return d;
};

const normalizeOptionList = (arr, fallbackSelect = true) => {
  const list = Array.isArray(arr) ? arr : [];

  const mapped = list.map((x) => {
    if (typeof x === "string") return { label: x, value: x };

    const label =
      x.label ??
      x.fld_branchname ??
      x.fld_vehiclecode ??
      x.fld_vehichlecode ??
      x.value ??
      "";

    const value =
      x.value ??
      x.fld_branchid ??
      x.fld_vehiclecode ??
      x.fld_vehichlecode ??
      x.label ??
      "";

    return {
      label: String(label),
      value: String(value),
    };
  });

  if (!fallbackSelect) return mapped;

  const hasSelect = mapped.some(
    (o) => toStr(o.value).toLowerCase() === "select"
  );

  return hasSelect ? mapped : [{ label: "Select", value: "Select" }, ...mapped];
};

const getCell = (r, keys) => {
  for (const k of keys) {
    const v = r?.[k];
    if (v !== null && v !== undefined && String(v).trim() !== "") return v;
  }
  return "-";
};

const VehicleHsdHourUpdationListing = () => {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined ? val : fallback;
  };

  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchIdLS =
    getLS("branchId") || getLS("Branchid") || getLS("branchid") || "0";
  const branchTypeLS = getLS("branchType") || getLS("branchtype") || "HO";
  const userIdLS =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId") || "0";
  const loginUserId =
    (typeof window !== "undefined"
      ? sessionStorage.getItem("loginUserId")
      : "") || userIdLS;

  const userCtx = useMemo(
    () => ({
      branchId: toInt(branchIdLS, 0),
      branchType: clean(branchTypeLS || "HO"),
      loginUserId: toInt(loginUserId, 0),
    }),
    [branchIdLS, branchTypeLS, loginUserId]
  );

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": userCtx.branchId,
      "x-branch": userCtx.branchType,
      "x-branchtype": userCtx.branchType,
      "x-userid": String(userIdLS || "").trim(),
    }),
    [finYearId, finYear, userCtx.branchId, userCtx.branchType, userIdLS]
  );

  const today = useMemo(() => new Date(), []);
  const defaultFromDate = useMemo(() => toYYYYMMDD(sixMonthsBack()), []);
  const defaultToDate = useMemo(() => toYYYYMMDD(today), [today]);

  const defaultFilters = useMemo(
    () => ({
      branch:
        userCtx.branchType === "HO" ? "Select" : String(userCtx.branchId || ""),
      vehicleCode: "Select",
      fromDate: defaultFromDate,
      toDate: defaultToDate,
    }),
    [userCtx.branchType, userCtx.branchId, defaultFromDate, defaultToDate]
  );

  const [showFilters, setShowFilters] = useState(true);
  const [dd, setDd] = useState({
    branches: [{ label: "Select", value: "Select" }],
    vehicleCodes: [{ label: "Select", value: "Select" }],
  });
  const [filters, setFilters] = useState(defaultFilters);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [pageLoading, setPageLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [vehicleCodesLoading, setVehicleCodesLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const isBusy = pageLoading || searching || exporting || vehicleCodesLoading;
  const loading = pageLoading || searching;

  const inputClass = useMemo(
    () =>
      `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
        isDark
          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () =>
      `block mb-2 text-sm font-medium ${
        isDark ? "text-gray-300" : "text-gray-700"
      }`,
    [isDark]
  );

  const tableHead = useMemo(
    () => [
      "S.No",
      "Date",
      "Issue Qty",
      "Vehicle Code:Name",
      "Pre HSD Starting Hr",
      "Cur HSD Starting Hr",
      "Pre HSD Closing Hr",
      "Cur HSD Closing Hr",
      "Pre HSD Starting KM",
      "Cur HSD Starting KM",
      "Pre HSD Closing KM",
      "Cur HSD Closing KM",
      "Updated Date",
      "User Name",
    ],
    []
  );

  const validateFilters = useCallback(
    (f = filters) => {
      if (userCtx.branchType === "HO" && isSelect(f.branch)) {
        toast.warn("Branch is required");
        return false;
      }
      if (!f.fromDate) {
        toast.warn("Please select From Date");
        return false;
      }
      if (!f.toDate) {
        toast.warn("Please select To Date");
        return false;
      }

      const fd = new Date(f.fromDate);
      const td = new Date(f.toDate);
      if (Number.isNaN(fd.getTime()) || Number.isNaN(td.getTime())) {
        toast.warn("Invalid date selection");
        return false;
      }
      if (fd > td) {
        toast.warn("From Date cannot be greater than To Date");
        return false;
      }
      return true;
    },
    [filters, userCtx.branchType]
  );

  const resetRows = useCallback(() => {
    setRows([]);
    setTotal(0);
  }, []);

  const syncRowsFromResponse = useCallback((data) => {
    const list = Array.isArray(data?.rows)
      ? data.rows
      : Array.isArray(data?.data)
      ? data.data
      : [];
    setRows(list);
    setTotal(toInt(data?.total, list.length));
  }, []);

  const loadInit = useCallback(async () => {
    try {
      setPageLoading(true);
      const { data } = await axios.get(`${API_BASE}/init`, {
        params: {
          branchId: userCtx.branchId,
          branchType: userCtx.branchType,
        },
        headers: commonHeaders,
      });

      if (!data?.success) {
        toast.error(data?.message || "Init failed");
        return;
      }

      setDd({
        branches: normalizeOptionList(data?.branches),
        vehicleCodes: normalizeOptionList(data?.vehicleCodes),
      });

      setFilters((prev) => ({
        ...prev,
        branch:
          userCtx.branchType === "HO"
            ? prev.branch || "Select"
            : String(userCtx.branchId),
      }));
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || e.message || "Init failed");
    } finally {
      setPageLoading(false);
    }
  }, [userCtx.branchId, userCtx.branchType, commonHeaders]);

  const loadVehicleCodesByBranch = useCallback(
    async (branchValue) => {
      try {
        setVehicleCodesLoading(true);

        const branch =
          userCtx.branchType !== "HO"
            ? String(userCtx.branchId)
            : isSelect(branchValue)
            ? "Select"
            : String(branchValue);

        const { data } = await axios.get(`${API_BASE}/vehicleCodes`, {
          params: { branch },
          headers: commonHeaders,
        });

        if (!data?.success) {
          toast.error(data?.message || "Vehicle codes load failed");
          setDd((prev) => ({
            ...prev,
            vehicleCodes: [{ label: "Select", value: "Select" }],
          }));
          return;
        }

        setDd((prev) => ({
          ...prev,
          vehicleCodes: normalizeOptionList(data?.vehicleCodes),
        }));
      } catch (e) {
        console.error(e);
        toast.error(
          e?.response?.data?.message ||
            e.message ||
            "Failed to load Vehicle Codes"
        );
        setDd((prev) => ({
          ...prev,
          vehicleCodes: [{ label: "Select", value: "Select" }],
        }));
      } finally {
        setVehicleCodesLoading(false);
      }
    },
    [userCtx.branchType, userCtx.branchId, commonHeaders]
  );

  const loadList = useCallback(
    async (override = {}) => {
      const f = { ...filters, ...override };
      if (!validateFilters(f)) return;

      try {
        setSearching(true);
        setHasSearched(true);

        const params = {
          branch:
            userCtx.branchType !== "HO"
              ? String(userCtx.branchId)
              : isSelect(f.branch)
              ? "Select"
              : String(f.branch),
          vehicleCode: isSelect(f.vehicleCode)
            ? "Select"
            : String(f.vehicleCode),
          fromDate: f.fromDate,
          toDate: f.toDate,
        };

        const { data } = await axios.get(`${API_BASE}/list`, {
          params,
          headers: commonHeaders,
        });

        if (!data?.success) {
          toast.error(data?.message || "List failed");
          resetRows();
          return;
        }

        syncRowsFromResponse(data);
        const list = Array.isArray(data?.rows)
          ? data.rows
          : Array.isArray(data?.data)
          ? data.data
          : [];
        if (list.length === 0) toast.info("No Records found");
      } catch (e) {
        console.error(e);
        toast.error(e?.response?.data?.message || e.message || "Search failed");
        resetRows();
      } finally {
        setSearching(false);
      }
    },
    [
      filters,
      validateFilters,
      userCtx.branchType,
      userCtx.branchId,
      commonHeaders,
      resetRows,
      syncRowsFromResponse,
    ]
  );

  const exportExcel = useCallback(async () => {
    if (!validateFilters()) return;

    try {
      setExporting(true);

      const params = {
        branch:
          userCtx.branchType !== "HO"
            ? String(userCtx.branchId)
            : isSelect(filters.branch)
            ? "Select"
            : String(filters.branch),
        vehicleCode: isSelect(filters.vehicleCode)
          ? "Select"
          : String(filters.vehicleCode),
        fromDate: filters.fromDate,
        toDate: filters.toDate,
      };

      const res = await axios.get(`${API_BASE}/export`, {
        params,
        headers: commonHeaders,
        responseType: "blob",
      });

      const blob = new Blob([res.data], {
        type: res.headers["content-type"] || "application/vnd.ms-excel",
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download =
        `HSD_Hr_Updation_${filters.fromDate}_to_${filters.toDate}.xls`.replace(
          /[:]/g,
          "-"
        );
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Excel exported");
    } catch (e) {
      console.error(e);
      toast.error(
        e?.response?.data?.message || e.message || "Excel export failed"
      );
    } finally {
      setExporting(false);
    }
  }, [
    validateFilters,
    userCtx.branchType,
    userCtx.branchId,
    filters.branch,
    filters.vehicleCode,
    filters.fromDate,
    filters.toDate,
    commonHeaders,
  ]);

  const handleAddNew = useCallback(() => {
    navigate("/inventory/vehicle-hsd-hr-updation");
  }, [navigate]);

  const handleClearFilters = useCallback(async () => {
    setFilters(defaultFilters);
    resetRows();
    setHasSearched(false);
    await loadVehicleCodesByBranch(defaultFilters.branch);
  }, [defaultFilters, resetRows, loadVehicleCodesByBranch]);

  const handleInputChange = useCallback((field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  }, []);

  const onBranchChange = useCallback(
    async (val) => {
      setFilters((prev) => ({
        ...prev,
        branch: val,
        vehicleCode: "Select",
      }));
      await loadVehicleCodesByBranch(val);
    },
    [loadVehicleCodesByBranch]
  );

  useEffect(() => {
    loadInit();
  }, [loadInit]);

  useEffect(() => {
    if (userCtx.branchType !== "HO" && userCtx.branchId) {
      loadVehicleCodesByBranch(String(userCtx.branchId));
    }
  }, [userCtx.branchType, userCtx.branchId, loadVehicleCodesByBranch]);

  return (
    <VehicleLayoutWrapper>
      <div
        className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}
      >
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">
              HSD HR UPDATION LISTING
            </span>
          }
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div>
              <h2
                className={`text-2xl font-bold ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                HSD Hr Updation Listing
              </h2>
              <p
                className={`text-sm mt-1 ${
                  isDark ? "text-gray-300" : "text-gray-500"
                }`}
              >
                INFRA SCREEN : A-39/1
                {hasSearched ? `  •  ${total} Records` : ""}
              </p>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={() => navigate(-1)}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaArrowLeft />
                Back
              </button>

              <button
                type="button"
                onClick={handleAddNew}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isBusy ? "opacity-60 cursor-not-allowed" : ""
                } ${buttonClass} ${hoverClass} text-white`}
              >
                <FaPlus />
                Add New
              </button>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {showFilters && (
            <div
              className={`p-6 rounded-lg mb-6 ${
                isDark ? "bg-gray-800/50" : "bg-blue-50"
              }`}
            >
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3
                  className={`text-lg font-semibold ${
                    isDark ? "text-white" : "text-blue-900"
                  }`}
                >
                  Search Criteria
                </h3>

                <div className="flex gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={handleClearFilters}
                    disabled={isBusy}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                  >
                    Clear
                  </button>

                  <button
                    type="button"
                    onClick={() => loadList()}
                    disabled={isBusy}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                      isBusy ? "opacity-60 cursor-not-allowed" : ""
                    } ${buttonClass} ${hoverClass} text-white`}
                  >
                    <FaSearch />
                    {searching ? "Searching..." : "Search"}
                  </button>

                  <button
                    type="button"
                    onClick={exportExcel}
                    disabled={isBusy}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                      isBusy ? "opacity-60 cursor-not-allowed" : ""
                    } ${buttonClass} ${hoverClass} text-white`}
                  >
                    <FaFileExcel />
                    {exporting ? "Exporting..." : "Excel"}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div>
                  <label className={labelClass}>
                    Branch <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={filters.branch}
                    onChange={(e) => onBranchChange(e.target.value)}
                    className={inputClass}
                    disabled={userCtx.branchType !== "HO" || isBusy}
                  >
                    {dd.branches.map((b) => (
                      <option key={String(b.value)} value={b.value}>
                        {b.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Vehicle Code</label>
                  <select
                    value={filters.vehicleCode}
                    onChange={(e) =>
                      handleInputChange("vehicleCode", e.target.value)
                    }
                    className={inputClass}
                    disabled={isBusy}
                  >
                    {dd.vehicleCodes.map((v) => (
                      <option key={String(v.value)} value={v.value}>
                        {v.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>
                    From Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={filters.fromDate}
                    onChange={(e) =>
                      handleInputChange("fromDate", e.target.value)
                    }
                    className={inputClass}
                    disabled={isBusy}
                  />
                </div>

                <div>
                  <label className={labelClass}>
                    To Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={filters.toDate}
                    onChange={(e) =>
                      handleInputChange("toDate", e.target.value)
                    }
                    className={inputClass}
                    disabled={isBusy}
                  />
                </div>
              </div>
            </div>
          )}

          <div className="overflow-x-auto rounded-lg border shadow-lg">
            {loading ? (
              <div
                className={`p-16 text-center ${
                  isDark ? "bg-gray-800" : "bg-gray-50"
                }`}
              >
                <div
                  className={`text-lg font-medium ${
                    isDark ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  {pageLoading
                    ? "Loading..."
                    : vehicleCodesLoading
                    ? "Loading vehicle codes..."
                    : "Searching..."}
                </div>
              </div>
            ) : rows.length === 0 ? (
              <div
                className={`p-16 text-center ${
                  isDark ? "bg-gray-800" : "bg-gray-50"
                }`}
              >
                <div
                  className={`text-lg font-medium mb-4 ${
                    isDark ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  {hasSearched
                    ? "No Records found"
                    : "Please search to view records"}
                </div>
                <button
                  type="button"
                  onClick={handleAddNew}
                  className={`inline-flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg ${buttonClass} ${hoverClass} text-white`}
                >
                  <FaPlus />
                  Add New
                </button>
              </div>
            ) : (
              <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                <thead className={`${tableHeaderClass} text-white`}>
                  <tr>
                    {tableHead.map((h) => (
                      <th
                        key={h}
                        className="px-6 py-4 text-left text-sm font-semibold whitespace-nowrap"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody
                  className={`${
                    isDark ? "divide-gray-700" : "divide-gray-200"
                  } divide-y`}
                >
                  {rows.map((r, idx) => (
                    <tr
                      key={idx}
                      className={`${
                        idx % 2 === 0
                          ? isDark
                            ? "bg-gray-800"
                            : "bg-white"
                          : isDark
                          ? "bg-gray-750"
                          : "bg-gray-50"
                      } transition-colors ${
                        isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"
                      }`}
                    >
                      <td
                        className={`px-6 py-4 text-sm ${
                          isDark ? "text-gray-300" : "text-gray-900"
                        }`}
                      >
                        {idx + 1}
                      </td>
                      <td
                        className={`px-6 py-4 text-sm ${
                          isDark ? "text-gray-300" : "text-gray-900"
                        }`}
                      >
                        {getCell(r, ["fld_SIdate", "fld_SIDate", "fld_Date", "Date"])}
                      </td>
                      <td
                        className={`px-6 py-4 text-sm font-semibold ${
                          isDark ? "text-green-400" : "text-green-600"
                        }`}
                      >
                        {getCell(r, ["fld_qty", "fld_Qty", "Qty"])}
                      </td>
                      <td
                        className={`px-6 py-4 text-sm font-medium ${
                          isDark ? "text-blue-400" : "text-blue-600"
                        }`}
                      >
                        {getCell(r, [
                          "fld_vehiclecode",
                          "fld_vehiclecodename",
                          "VehicleCode",
                        ])}
                      </td>
                      <td
                        className={`px-6 py-4 text-sm ${
                          isDark ? "text-gray-300" : "text-gray-900"
                        }`}
                      >
                        {getCell(r, ["fld_Pre_HSDStartingHr", "fld_preStartingHr"])}
                      </td>
                      <td
                        className={`px-6 py-4 text-sm ${
                          isDark ? "text-gray-300" : "text-gray-900"
                        }`}
                      >
                        {getCell(r, ["fld_Cur_HSDStartingHr", "fld_curStartingHr"])}
                      </td>
                      <td
                        className={`px-6 py-4 text-sm ${
                          isDark ? "text-gray-300" : "text-gray-900"
                        }`}
                      >
                        {getCell(r, ["fld_Pre_HSDClosingHr", "fld_preClosingHr"])}
                      </td>
                      <td
                        className={`px-6 py-4 text-sm ${
                          isDark ? "text-gray-300" : "text-gray-900"
                        }`}
                      >
                        {getCell(r, ["fld_Cur_HSDClosingHr", "fld_curClosingHr"])}
                      </td>
                      <td
                        className={`px-6 py-4 text-sm ${
                          isDark ? "text-gray-300" : "text-gray-900"
                        }`}
                      >
                        {getCell(r, ["fld_Pre_HSDStartingKM", "fld_preStartingKM"])}
                      </td>
                      <td
                        className={`px-6 py-4 text-sm ${
                          isDark ? "text-gray-300" : "text-gray-900"
                        }`}
                      >
                        {getCell(r, ["fld_Cur_HSDStartingKM", "fld_curStartingKM"])}
                      </td>
                      <td
                        className={`px-6 py-4 text-sm ${
                          isDark ? "text-gray-300" : "text-gray-900"
                        }`}
                      >
                        {getCell(r, ["fld_Pre_HSDClosingKM", "fld_preClosingKM"])}
                      </td>
                      <td
                        className={`px-6 py-4 text-sm ${
                          isDark ? "text-gray-300" : "text-gray-900"
                        }`}
                      >
                        {getCell(r, ["fld_Cur_HSDClosingKM", "fld_curClosingKM"])}
                      </td>
                      <td
                        className={`px-6 py-4 text-sm ${
                          isDark ? "text-gray-300" : "text-gray-900"
                        }`}
                      >
                        {getCell(r, ["fld_date", "fld_Date", "UpdatedDate"])}
                      </td>
                      <td
                        className={`px-6 py-4 text-sm font-medium ${
                          isDark ? "text-white" : "text-gray-900"
                        }`}
                      >
                        {getCell(r, ["fld_username", "UserName"])}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default VehicleHsdHourUpdationListing;